import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Stack,
  CircularProgress, Alert, Divider, Paper,
} from '@mui/material';
import {
  Paid as PaidIcon,
  HourglassEmpty as PendingIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import PeriodFilter from '../PeriodFilter';
import { getPeriodRange } from '../../utils/periodRange';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
};

const formatDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const SummaryCard = ({ label, value, color }) => (
  <Paper variant="outlined" sx={{ p: 1.5, flex: 1, borderRadius: 2, textAlign: 'center' }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={700} color={color}>
      {value}
    </Typography>
  </Paper>
);

/**
 * MaidEarningsView — "Earnings" tab of the maid portal.
 *
 * Reads the maid's own jobs (same endpoint as the schedule) and surfaces the
 * `myAssignment` payment fields: amount, paid/pending status, and paid date.
 * Totals are computed client-side for the selected period.
 */
const MaidEarningsView = () => {
  const { user } = useAuth();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [periodType, setPeriodType] = useState('month'); // earnings read better by month
  const [anchor, setAnchor] = useState(new Date());

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => getPeriodRange(periodType, anchor),
    [periodType, anchor]
  );

  const fetchJobs = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/jobs/maid/${user.id}`, {
        params: {
          scheduledStartMin: rangeStart.toISOString(),
          scheduledStartMax: rangeEnd.toISOString(),
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = response.data;
      setJobs(Array.isArray(data) ? data : data.jobs || []);
    } catch (err) {
      logger.log('Error fetching earnings jobs:', err);
      setError('Could not load your earnings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, rangeStart, rangeEnd]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Only jobs that carry a payment amount count toward earnings
  const earningJobs = useMemo(
    () =>
      jobs
        .filter((j) => j.status !== 'CANCELLED' && j.myAssignment?.paymentAmount != null)
        .sort((a, b) => new Date(b.utcScheduledDate || 0) - new Date(a.utcScheduledDate || 0)),
    [jobs]
  );

  const totals = useMemo(() => {
    let paid = 0;
    let pending = 0;
    earningJobs.forEach((j) => {
      const amount = Number(j.myAssignment.paymentAmount) || 0;
      if (j.myAssignment.paid) paid += amount;
      else pending += amount;
    });
    return { paid, pending, total: paid + pending };
  }, [earningJobs]);

  return (
    <Box>
      <PeriodFilter
        periodType={periodType}
        anchor={anchor}
        start={rangeStart}
        end={rangeEnd}
        onPeriodTypeChange={setPeriodType}
        onAnchorChange={setAnchor}
      />

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {!loading && !error && (
        <>
          {/* Totals */}
          <Stack direction="row" spacing={1.5} mb={2}>
            <SummaryCard label="Total" value={formatMoney(totals.total)} color="text.primary" />
            <SummaryCard label="Paid" value={formatMoney(totals.paid)} color="success.main" />
            <SummaryCard label="Pending" value={formatMoney(totals.pending)} color="warning.main" />
          </Stack>

          {earningJobs.length === 0 ? (
            <Box textAlign="center" py={6}>
              <Typography color="text.secondary">
                No earnings recorded for this {periodType}.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Payment amounts appear here once jobs are assigned to you.
              </Typography>
            </Box>
          ) : (
            earningJobs.map((job) => {
              const a = job.myAssignment;
              const customerName =
                job.customer?.customerName ||
                `${job.customer?.firstName || ''} ${job.customer?.lastName || ''}`.trim() ||
                'Customer';
              return (
                <Card variant="outlined" key={job.id} sx={{ mb: 1.5 }}>
                  <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box minWidth={0}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {customerName}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                          <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(job.utcScheduledDate) || 'TBD'}
                            {job.jobNumber ? ` · #${job.jobNumber}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                      <Box textAlign="right" flexShrink={0}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {formatMoney(a.paymentAmount)}
                        </Typography>
                        {a.paid ? (
                          <Chip
                            icon={<PaidIcon />}
                            label={a.paidDate ? `Paid ${formatDate(a.paidDate)}` : 'Paid'}
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" />
                        )}
                      </Box>
                    </Box>
                    {job.status === 'COMPLETED' && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          Job completed{job.actualDuration ? ` in ${job.actualDuration} min` : ''}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </>
      )}
    </Box>
  );
};

export default MaidEarningsView;
